/**
 * Gated render functions — visible only after email gate unlock.
 * All wrap output in <section class="section gated-section">.
 */

import { esc, fmtCompact, gizmoLabel, buildGizmoLabels } from './shared.js';

// ============================================================
// Section 7: Chat Fitness Plan (5 Fixes)
// ============================================================

export function renderFitnessPlans(result) {
  const fixes = [];
  const gizmoLabels = buildGizmoLabels(result.conversations);

  // Fix 1: Start Fresh on These Chats
  const redZone = result.conversations
    .filter(c => c.bloatScore >= 1.0)
    .sort((a, b) => b.bloatScore - a.bloatScore);
  if (redZone.length > 0) {
    fixes.push(renderFix(1, 'Start Fresh on These Chats',
      `${redZone.length} of your chats have exceeded their context window. The model can't see your earlier instructions anymore.`,
      {
        steps: [
          {
            instruction: 'Paste this into the bloated chat to extract a topic map:',
            prompt: `Construct a structured topic map of this entire conversation.\n\nFor each topic include:\n- Title\n- 2\u20134 sentence summary\n- Approximate share of the conversation (low / medium / high)\n- Key decisions made\n- Open loops or unresolved items\n- Your confidence this reflects full detail rather than a compressed abstraction`,
          },
          {
            instruction: 'Pick a topic from the map, then paste this in the same chat to extract the full detail:',
            prompt: `Give me a complete extraction for the topic: [topic title from the map]\n\nInclude:\n- Full reasoning arc (how the approach evolved, what was tried and rejected)\n- All constraints, requirements, and numeric thresholds\n- Key decisions with the rationale behind each\n- Current state and open items\n- Exact terminology, names, or identifiers I should preserve\n\nBe thorough \u2014 this will be the only record. Repeat for each topic I ask about.`,
          },
          {
            instruction: 'For each extracted topic, start a new chat and paste:',
            prompt: `I'm reconstructing a conversation that outgrew its context window. Here is the full topic extraction:\n\n[paste the detailed extraction from Step 2]\n\nReconstruct the working context: restate the constraints, confirm the decisions, and pick up from the current state. Flag anything that seems incomplete or ambiguous so I can fill in gaps.`,
          },
        ],
        limitation: 'ChatGPT may have already compressed earlier parts of this chat. The topic map will reflect what\u2019s currently retained, not necessarily the original detail.',
      },
      redZone.slice(0, 5).map(c => ({
        title: c.title,
        stat: `${(c.bloatScore * 100).toFixed(0)}% bloat \u00b7 ${c.messageCount} msgs`
      })),
      redZone.length
    ));
  }

  // Fix 2: Stop Talking to the Void
  const ghostChats = result.conversations
    .filter(c => c.ghostWastedMessages > 0)
    .sort((a, b) => b.ghostWastedMessages - a.ghostWastedMessages);
  if (ghostChats.length > 0) {
    fixes.push(renderFix(2, 'Stop Talking to the Void',
      `${ghostChats.length} chats have messages sent after the context window overflowed. The model responded, but it couldn't see your earlier work.`,
      {
        steps: [
          {
            instruction: 'Paste this into the ghost zone chat to see what\u2019s been lost:',
            prompt: `List all governing constraints, numeric thresholds, design principles, and fixed decisions established in this conversation.\n\nThen tell me: what topics from early in this conversation may have been compressed or lost due to context limits? What assumptions from early on may no longer be active but are still influencing your responses?`,
          },
          {
            instruction: 'Review what\u2019s missing, then paste this to create a carry-forward brief:',
            prompt: `Based on what you can still see, create a carry-forward brief I can paste into a fresh chat. Include:\n- The core goal of this conversation\n- All constraints and decisions still active\n- Current state and open items\n- Anything you're uncertain about \u2014 flag it explicitly`,
          },
        ],
        limitation: 'You\u2019ve been sending messages into this chat\u2019s blind spot. ChatGPT responded, but couldn\u2019t see your earlier instructions. The recovery will be partial.',
      },
      ghostChats.slice(0, 5).map(c => ({
        title: c.title,
        stat: `${c.ghostWastedMessages} ghost msgs \u00b7 starts at msg #${c.ghostZoneStartIndex || '?'}`
      })),
      ghostChats.length
    ));
  }

  // Fix 3: Create Custom Instructions for These Topics
  const reInstructed = result.conversations
    .filter(c => c.reinstruction && c.reinstruction.reinstructionCount > 0)
    .sort((a, b) => b.reinstruction.reinstructionRate - a.reinstruction.reinstructionRate);
  if (reInstructed.length > 0) {
    fixes.push(renderFix(3, 'Create Custom Instructions for These Topics',
      `You re-explained yourself ${reInstructed.reduce((s, c) => s + c.reinstruction.reinstructionCount, 0)} times across ${reInstructed.length} chats. Custom Instructions remember for you.`,
      {
        steps: [
          {
            instruction: 'Paste this into any affected chat to extract your repeated context:',
            prompt: `Scan this conversation for context, preferences, or instructions I've explained or reminded you about more than once.\n\nThen draft two things:\n1. A Custom Instructions block (under 1500 characters) I can paste into Settings \u2192 Personalization \u2192 Custom Instructions\n2. Any items better suited as individual Memory entries\n\nFocus only on what I keep repeating \u2014 not general preferences you'd assume for any user.`,
          },
        ],
        limitation: 'Custom Instructions apply across all your chats. Review the draft before saving \u2014 make sure it\u2019s specific to your workflow, not just this conversation.',
      },
      reInstructed.slice(0, 5).map(c => ({
        title: c.title,
        stat: `${c.reinstruction.reinstructionCount} re-instructions \u00b7 ${(c.reinstruction.reinstructionRate * 100).toFixed(0)}% rate`
      })),
      reInstructed.length
    ));
  }

  // Fix 4: Consolidate These Restart Patterns
  const restartGroups = result.crossChat?.topicRestarts?.restartGroups || [];
  if (restartGroups.length > 0) {
    fixes.push(renderFix(4, 'Consolidate These Restart Patterns',
      `${restartGroups.length} topics appear in multiple chats. Each restart re-establishes context from scratch.`,
      {
        steps: [
          {
            instruction: 'Paste this into the most active chat on the topic:',
            prompt: `I keep starting new conversations about this topic instead of continuing in one place.\n\nHelp me create a Project Brief I can use as a ChatGPT Project so the context persists:\n1. Project name and scope\n2. Persistent instructions (under 2000 characters) capturing the shared context\n3. Key decisions and constraints established across our conversations\n4. Reference material I should upload to the Project\n\nFormat the instructions block so I can paste it directly into a new ChatGPT Project.`,
          },
        ],
        limitation: 'This captures what ChatGPT remembers from this chat. If you\u2019ve discussed this topic in other chats too, review those separately.',
      },
      restartGroups.slice(0, 5).map(g => ({
        title: g.signal === 'gizmo' ? gizmoLabel(g.key, gizmoLabels) : g.key,
        stat: `${g.count} chats \u00b7 ${g.signal}`
      })),
      restartGroups.length
    ));
  }

  // Fix 5: Archive the Dead Weight
  const orphans = result.crossChat?.orphanedChats;
  if (orphans && orphans.orphanedCount > 0) {
    fixes.push(renderFix(5, 'Archive the Dead Weight',
      `${orphans.orphanedCount} chats are orphaned \u2014 abandoned starts, single exchanges, or one-off GPT uses.`,
      'Archive these to clean up your sidebar. Less clutter, faster navigation.',
      [
        { title: 'Single exchange', stat: `${orphans.singleExchange} chats` },
        { title: 'Abandoned starts', stat: `${orphans.abandonedStart} chats` },
        { title: 'With shared GPT', stat: `${orphans.orphanedWithGizmo} chats` },
      ],
      0
    ));
  }

  // Fix 6: Hem the Long Ones
  const tallChats = result.conversations
    .filter(c => c.messageCount >= 100 && c.bloatScore < 1.0)
    .sort((a, b) => b.messageCount - a.messageCount);
  if (tallChats.length > 0) {
    fixes.push(renderFix(6, 'Hem the Long Ones',
      `${tallChats.length} ${tallChats.length === 1 ? 'chat has' : 'chats have'} 100+ messages but haven\u2019t hit the context window. The model can still see everything \u2014 but ChatGPT\u2019s UI is rendering every message bubble, code block, and image as a separate DOM element. That\u2019s why ${tallChats.length === 1 ? 'it feels' : 'they feel'} slow.`,
      {
        noStepNumbers: true,
        steps: [
          {
            label: 'Option A \u2014',
            instruction: 'Keep everything. Select all the messages in the chat (or find the conversation in your export archive), copy the full text, and paste it as a single message into a new chat. Hundreds of turns become one DOM node \u2014 same context, instant scrolling.',
          },
          {
            label: 'Option B \u2014',
            instruction: 'Compress it. Paste this into the long chat to get a carry-forward brief:',
            prompt: `This conversation has grown long and the UI is slow. Create a comprehensive carry-forward brief I can paste into a new chat.\n\nInclude:\n- The core goal and current state\n- All active constraints and decisions\n- Key context that would be lost if I started over\n- Open items and next steps\n\nOptimize for completeness \u2014 I won\u2019t have access to this conversation\u2019s history in the new chat.`,
          },
        ],
        limitation: 'Option A works best for conversations well within the context window. For chats closer to the limit, Option B is safer \u2014 the compressed brief leaves room for new work.',
      },
      tallChats.slice(0, 5).map(c => ({
        title: c.title,
        stat: `${c.messageCount} msgs \u00b7 ${(c.bloatScore * 100).toFixed(0)}% bloat`
      })),
      tallChats.length
    ));
  }

  if (fixes.length === 0) return '';

  return `
    <section class="section gated-section" id="section-fitness">
      <div class="section-label">Chat Fitness Plan</div>
      <div class="section-title">Your personalized fixes</div>
      <div class="section-desc">
        Based on your actual ChatGPT usage. Each fix targets a specific pattern draining your time.
      </div>

      ${fixes.join('')}

      <div class="soft-bridge">
        <p>These recipes work on what ChatGPT currently remembers &mdash; which may already be compressed. For a full-fidelity refactor from your raw archive, <strong>ChatFit</strong> reconstructs your thinking from the complete record, not from what the UI happens to retain.</p>
      </div>
    </section>
  `;
}

function renderFix(num, title, desc, action, examples, totalCount) {
  const exampleRows = examples.map(ex => `
    <div class="fix-example-row">
      <span class="fix-example-title">${esc(ex.title)}</span>
      <span class="fix-example-stat">${esc(ex.stat)}</span>
    </div>
  `).join('');

  const seeAll = totalCount > 5
    ? `<div class="fix-see-all">${totalCount - 5} more &mdash; see all in admin view</div>`
    : '';

  // Fix 5 (and any legacy) passes a plain string — render as before
  const actionBlock = typeof action === 'string'
    ? `<div class="fix-action">${esc(action)}</div>`
    : renderRecipe(action);

  return `
    <div class="fix-card">
      <div class="fix-number">Fix ${num}</div>
      <div class="fix-title">${esc(title)}</div>
      <div class="fix-desc">${desc}</div>
      <div class="fix-examples">
        ${exampleRows}
        ${seeAll}
      </div>
      ${actionBlock}
    </div>
  `;
}

function renderRecipe(recipe) {
  const autoNumber = recipe.steps.length > 1 && !recipe.noStepNumbers;
  const steps = recipe.steps.map((step, i) => {
    const stepNum = autoNumber ? `<span class="fix-step-num">Step ${i + 1}.</span> `
      : step.label ? `<span class="fix-step-num">${esc(step.label)}</span> `
      : '';
    const promptBlock = step.prompt ? `
        <div class="fix-recipe-prompt-wrap">
          <pre class="fix-recipe-prompt">${esc(step.prompt)}</pre>
          <button class="fix-copy-btn" type="button" aria-label="Copy prompt">Copy</button>
        </div>` : '';
    return `
      <div class="fix-recipe-step">
        <div class="fix-recipe-instruction">${stepNum}${esc(step.instruction)}</div>
        ${promptBlock}
      </div>
    `;
  }).join('');

  return `
    <div class="fix-recipe">
      ${steps}
      ${recipe.limitation ? `<div class="fix-limitation">${esc(recipe.limitation)}</div>` : ''}
    </div>
  `;
}

export function initFixPrompts() {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.fix-copy-btn');
    if (!btn) return;
    const prompt = btn.parentElement.querySelector('.fix-recipe-prompt');
    if (!prompt) return;
    navigator.clipboard.writeText(prompt.textContent).then(() => {
      btn.textContent = 'Copied!';
      btn.classList.add('copied');
      setTimeout(() => {
        btn.textContent = 'Copy';
        btn.classList.remove('copied');
      }, 2000);
    });
  });
}

// ============================================================
// Section 8: Fattest Chats (top 10 detail table)
// ============================================================

function washClass(score) {
  const pct = score * 100;
  if (pct <= 25) return 'wash-1';
  if (pct <= 50) return 'wash-2';
  if (pct <= 75) return 'wash-3';
  if (pct <= 100) return 'wash-4';
  return 'wash-5';
}

export function renderFattestChats(result) {
  if (result.topBloated.length === 0) return '';

  const rows = result.topBloated.map((conv, i) => {
    const score = conv.adjustedBloatScore || conv.bloatScore;
    const barWidth = Math.min(score * 25, 100);
    const wash = washClass(score);
    const scorePct = (score * 100).toFixed(0);
    const contentType = conv.renderProfile?.dominantContentType || 'text';

    return `
      <tr class="wash-border-${wash.split('-')[1]}">
        <td class="num">${i + 1}</td>
        <td class="title-cell">${esc(conv.title)}</td>
        <td><span class="content-badge ${contentType}">${contentType}</span></td>
        <td>
          <div class="bloat-meter">
            <div class="bloat-meter-track">
              <div class="bloat-meter-fill ${wash}" style="width:${barWidth}%"></div>
            </div>
            <span class="bloat-meter-val" style="color:var(--${wash})">${scorePct}%</span>
          </div>
        </td>
        <td class="num">${conv.totalTokens.toLocaleString()}</td>
        <td class="num">${conv.messageCount}</td>
        <td class="num">${conv.chatTax ? (conv.chatTax.score * 100).toFixed(0) + '%' : '\u2014'}</td>
        <td class="num">${conv.modelSlug || '\u2014'}</td>
      </tr>
    `;
  }).join('');

  return `
    <section class="section gated-section" id="section-fattest">
      <div class="section-label">Fattest Chats</div>
      <div class="section-title">Top ${result.topBloated.length} by bloat score</div>

      <div class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Type</th>
              <th>Bloat</th>
              <th>Tokens</th>
              <th>Msgs</th>
              <th>Tax</th>
              <th>Model</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </section>
  `;
}

// ============================================================
// Section 9: The Void (Ghost Waste detail)
// ============================================================

export function renderGhostWaste(result) {
  const totalGhostWasted = result.conversations.reduce((s, c) => s + (c.ghostWastedMessages || 0), 0);
  const totalContextWasted = result.crossChat?.totalWastedMessages || 0;
  const avgWasteRate = result.crossChat?.avgContextWasteRate || 0;
  const ghostConvs = result.conversations.filter(c => c.ghostWastedMessages > 0).length;
  const wasteConvs = result.conversations.filter(c => c.contextWaste && c.contextWaste.wastedMessageCount > 0).length;

  if (totalGhostWasted === 0 && totalContextWasted === 0) return '';

  const topGhost = result.conversations
    .filter(c => c.ghostWastedMessages > 0)
    .sort((a, b) => b.ghostWastedMessages - a.ghostWastedMessages)
    .slice(0, 5);

  const topContextWaste = result.conversations
    .filter(c => c.contextWaste && c.contextWaste.contextWasteRate > 0.1)
    .sort((a, b) => b.contextWaste.contextWasteRate - a.contextWaste.contextWasteRate)
    .slice(0, 5);

  return `
    <section class="section gated-section" id="section-waste">
      <div class="section-label">The Void</div>
      <div class="section-title">Messages ChatGPT never heard</div>
      <div class="section-desc">
        Once a chat overflows the context window, your messages still get responses &mdash;
        but the model can no longer see your earlier instructions.
      </div>

      <div class="metric-grid">
        ${totalGhostWasted > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Ghost zone messages</div>
          <div class="metric-value">${totalGhostWasted.toLocaleString()}</div>
          <div class="metric-detail">sent after context overflow in ${ghostConvs} chats</div>
        </div>` : ''}
        ${totalContextWasted > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Unheard messages</div>
          <div class="metric-value">${totalContextWasted.toLocaleString()}</div>
          <div class="metric-detail">no echo in ${wasteConvs} conversations</div>
        </div>` : ''}
        ${avgWasteRate > 0 ? `
        <div class="metric-card">
          <div class="metric-label">Avg void rate</div>
          <div class="metric-value">${(avgWasteRate * 100).toFixed(1)}<span class="unit">%</span></div>
          <div class="metric-detail">of user messages go unheard</div>
        </div>` : ''}
      </div>

      ${topGhost.length > 0 ? `
      <div class="subsection">
        <div class="section-label">Most messages into the void</div>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr><th>Title</th><th>Ghost msgs</th><th>Void rate</th><th>Total msgs</th></tr>
            </thead>
            <tbody>
              ${topGhost.map(c => `
                <tr>
                  <td class="title-cell">${esc(c.title)}</td>
                  <td class="num">${c.ghostWastedMessages}</td>
                  <td class="num">${(c.ghostWasteRate * 100).toFixed(0)}%</td>
                  <td class="num">${c.messageCount}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
      ` : ''}

      ${topContextWaste.length > 0 ? `
      <div class="subsection">
        <div class="section-label">Most unheard messages</div>
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr><th>Title</th><th>Unheard</th><th>Unheard rate</th><th>User msgs</th></tr>
            </thead>
            <tbody>
              ${topContextWaste.map(c => `
                <tr>
                  <td class="title-cell">${esc(c.title)}</td>
                  <td class="num">${c.contextWaste.wastedMessageCount}</td>
                  <td class="num">${(c.contextWaste.contextWasteRate * 100).toFixed(0)}%</td>
                  <td class="num">${c.contextWaste.userMessageCount}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
      ` : ''}
    </section>
  `;
}

// ============================================================
// Section 10: Cross-Chat Patterns
// ============================================================

export function renderPatterns(result) {
  const cc = result.crossChat;
  const gizmoLabels = buildGizmoLabels(result.conversations);

  const affectedCount = result.conversations
    .filter(c => c.reinstruction && c.reinstruction.reinstructionCount > 0).length;

  const topRe = result.conversations
    .filter(c => c.reinstruction && c.reinstruction.reinstructionCount > 0)
    .sort((a, b) => b.reinstruction.reinstructionRate - a.reinstruction.reinstructionRate)
    .slice(0, 5);

  const topGroups = cc.topicRestarts.restartGroups.slice(0, 5);

  return `
    <section class="section gated-section" id="section-patterns">
      <div class="section-label">Bad Habits</div>
      <div class="section-title">Cross-chat patterns</div>

      <div class="metric-grid" style="margin-bottom:2rem">
        <div class="metric-card">
          <div class="metric-label">Re-instruction rate</div>
          <div class="metric-value">${(cc.avgReinstructionRate * 100).toFixed(1)}<span class="unit">%</span></div>
          <div class="metric-detail">${affectedCount} chats affected</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Topic restarts</div>
          <div class="metric-value">${cc.topicRestarts.totalRestartGroups}</div>
          <div class="metric-detail">${cc.topicRestarts.totalRestartConversations} chats in groups</div>
        </div>
        <div class="metric-card">
          <div class="metric-label">Orphaned chats</div>
          <div class="metric-value">${cc.orphanedChats.orphanedCount}</div>
          <div class="metric-detail">${cc.orphanedChats.singleExchange} single exchange</div>
        </div>
      </div>

      ${topRe.length > 0 ? `
        <div class="subsection">
          <div class="section-label">Highest re-instruction rates</div>
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr><th>Title</th><th>Rate</th><th>Flags</th><th>User msgs</th></tr>
              </thead>
              <tbody>
                ${topRe.map(c => `
                  <tr>
                    <td class="title-cell">${esc(c.title)}</td>
                    <td class="num">${(c.reinstruction.reinstructionRate * 100).toFixed(0)}%</td>
                    <td class="num">${c.reinstruction.reinstructionCount}</td>
                    <td class="num">${c.reinstruction.userMessageCount}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      ` : ''}

      ${topGroups.length > 0 ? `
        <div class="subsection">
          <div class="section-label">Top restart groups</div>
          <div class="table-wrap">
            <table class="data-table">
              <thead>
                <tr><th>Signal</th><th>Key</th><th>Chats</th></tr>
              </thead>
              <tbody>
                ${topGroups.map(g => `
                  <tr>
                    <td>${esc(g.signal)}</td>
                    <td class="title-cell">${esc(g.signal === 'gizmo' ? gizmoLabel(g.key, gizmoLabels) : g.key)}</td>
                    <td class="num">${g.count}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        </div>
      ` : ''}
    </section>
  `;
}
